/**
 * Cornerstone hit-tests a tool's annotations in array order and stops at the
 * first one near the pointer, while it draws them in the same order, so the
 * one painted on top is tested last. Put the selected annotation first, then
 * the rest top-most first, so a click lands on what the reader sees.
 */
export function orderForHitTest<T extends { annotationUID?: string }>(
  annotations: T[],
  selectedUID: string | null
): T[] {
  const isSelected = (a: T) => a.annotationUID === selectedUID;
  return [...annotations]
    .reverse()
    .sort((a, b) => Number(isSelected(b)) - Number(isSelected(a)));
}

/**
 * Cornerstone highlights every annotation near the pointer. While the selected
 * one is among them, clear the others so its label and handles are the only
 * hover artifacts. Returns true when anything changed.
 */
export function keepHoverOnSelected<
  T extends { annotationUID?: string; highlighted?: boolean },
>(annotations: T[], selectedUID: string | null): boolean {
  const selected = annotations.find((a) => a.annotationUID === selectedUID);
  if (!selected?.highlighted) return false;
  let changed = false;
  annotations.forEach((a) => {
    if (a !== selected && a.highlighted) {
      // Cornerstone's own hover pass writes this flag on the live annotation.
      // eslint-disable-next-line no-param-reassign
      a.highlighted = false;
      changed = true;
    }
  });
  return changed;
}
